import React from 'react';
import styled, { keyframes } from 'styled-components';

// Shimmer animation
const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

// Base skeleton component
const SkeletonBase = styled.div`
  background: linear-gradient(
    90deg,
    ${props => props.theme.colors.gray[200]} 25%,
    ${props => props.theme.colors.gray[100]} 50%,
    ${props => props.theme.colors.gray[200]} 75%
  );
  background-size: 2000px 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${props => props.theme.borderRadius.md};
`;

// Skeleton card for course/blog cards
const SkeletonCard = styled.div`
  background: ${props => props.theme.colors.light};
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: ${props => props.theme.shadows.md};
  overflow: hidden;
`;

const SkeletonImage = styled(SkeletonBase)`
  height: 200px;
  width: 100%;
  border-radius: 0;
`;

const SkeletonContent = styled.div`
  padding: ${props => props.theme.spacing.xl};
`;

const SkeletonTitle = styled(SkeletonBase)`
  height: 24px;
  width: 80%;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const SkeletonText = styled(SkeletonBase)`
  height: 16px;
  width: ${props => props.width || '100%'};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const SkeletonButton = styled(SkeletonBase)`
  height: 44px;
  width: 100%;
  margin-top: ${props => props.theme.spacing.lg};
`;

// Skeleton for course card
export const CourseCardSkeleton = () => (
  <SkeletonCard>
    <SkeletonImage />
    <SkeletonContent>
      <SkeletonTitle />
      <SkeletonText width="100%" />
      <SkeletonText width="90%" />
      <SkeletonText width="70%" />
      <div style={{ display: 'flex', gap: '1rem', margin: '1rem 0' }}>
        <SkeletonText width="60px" />
        <SkeletonText width="80px" />
      </div>
      <SkeletonButton />
    </SkeletonContent>
  </SkeletonCard>
);

// Skeleton for blog card
export const BlogCardSkeleton = () => (
  <SkeletonCard>
    <SkeletonImage />
    <SkeletonContent>
      <SkeletonTitle />
      <SkeletonText width="100%" />
      <SkeletonText width="95%" />
      <SkeletonText width="85%" />
      <div style={{ display: 'flex', gap: '1rem', margin: '1rem 0' }}>
        <SkeletonText width="100px" />
        <SkeletonText width="80px" />
      </div>
      <SkeletonText width="120px" />
    </SkeletonContent>
  </SkeletonCard>
);

// Skeleton grid
export const SkeletonGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing['3xl']};

  @media (max-width: ${props => props.theme.breakpoints.desktop}) {
    grid-template-columns: repeat(2, 1fr);
    gap: ${props => props.theme.spacing.xl};
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.lg};
  }
`;

// Generic skeleton loader
export const SkeletonLoader = ({ count = 3, type = 'course' }) => {
  const SkeletonComponent = type === 'blog' ? BlogCardSkeleton : CourseCardSkeleton;
  
  return (
    <SkeletonGrid>
      {[...Array(count)].map((_, index) => (
        <SkeletonComponent key={index} />
      ))}
    </SkeletonGrid>
  );
};

export default SkeletonLoader;
